import Button from '@atoms/Button/Button';
import Text from '@atoms/Text/Text';
import Toggle from '@atoms/Toggle/Toggle';
import DrawerWrapper from '@molecules/DrawerWrapper/DrawerWrapper';
import WallsFormSection, {
  PaintCalculatorLabels,
} from '@molecules/WallsForm/WallsForm';
import { calculatePaint, PaintResult } from '@utility/helpers/calculatePaint';
import { useCallback, useEffect, useReducer, useState } from 'react';
import {
  ActionType,
  initialState,
  PaintCalculatorState,
  reducer,
} from './reducer';

export interface IMakeCrazyCalculation {
  readonly text: string;
  readonly url: string;
  readonly target?: string;
}
export interface PaintCalculatorProps {
  readonly paintCalculator: IPaintCalculator;
  readonly productName: string;
  readonly productUrl: string;
}
export interface IPaintCalculator {
  readonly addAnotherAreaCtaLabel: string;
  readonly addAnotherWallCtaLabel: string;
  readonly addDoorAndWindowsCtaLabel: string;
  readonly byAreaTabDescription: string;
  readonly byAreaTabLabel: string;
  readonly bySurfaceTabDescription: string;
  readonly bySurfaceTabLabel: string;
  readonly ctaLabel: string;
  readonly decreaseAriaLabel: string;
  readonly doorsLabel: string;
  readonly estimatedLabel: string;
  readonly heightLabel: string;
  readonly heightOrWidthLabel: string;
  readonly increaseAriaLabel: string;
  readonly lengthLabel: string;
  readonly litresLabel: string;
  readonly makeCrazyCalculation: IMakeCrazyCalculation;
  readonly metricFeetLabel: string;
  readonly metricMeterLabel: string;
  readonly metricMeterSquareLabel: string;
  readonly paintCoverageLabel: string;
  readonly productPaintCoatsValue?: string;
  readonly productPaintCoverageValue?: string;
  readonly removeAreaCtaLabel: string;
  readonly removeWallCtaLabel: string;
  readonly resultDisclaimerText: string;
  readonly sendToEmailCtaLabel: string;
  readonly shareWhatsAppCtaLabel: string;
  readonly shareYouWillNeedLabel: string;
  readonly title: string;
  readonly unitTextLabel: string;
  readonly weThinkWillNeedLabel: string;
  readonly widthLabel: string;
  readonly windowsLabel: string;
}

const PaintCalculator = ({
  paintCalculator,
  productName,
  productUrl,
}: PaintCalculatorProps) => {
  const {
    addAnotherAreaCtaLabel,
    addAnotherWallCtaLabel,
    addDoorAndWindowsCtaLabel,
    byAreaTabDescription,
    byAreaTabLabel,
    bySurfaceTabDescription,
    bySurfaceTabLabel,
    ctaLabel,
    decreaseAriaLabel,
    doorsLabel,
    estimatedLabel,
    heightLabel,
    heightOrWidthLabel,
    increaseAriaLabel,
    lengthLabel,
    litresLabel,
    makeCrazyCalculation,
    metricFeetLabel,
    metricMeterLabel,
    metricMeterSquareLabel,
    paintCoverageLabel,
    productPaintCoatsValue,
    productPaintCoverageValue,
    removeAreaCtaLabel,
    removeWallCtaLabel,
    resultDisclaimerText,
    sendToEmailCtaLabel,
    shareWhatsAppCtaLabel,
    shareYouWillNeedLabel,
    title,
    unitTextLabel,
    weThinkWillNeedLabel,
    widthLabel,
    windowsLabel,
  } = paintCalculator;

  const [
    {
      activeTab,
      doorsCount,
      estimatedArea,
      estimatedLitres,
      showOpenings,
      unit,
      walls,
      windowsCount,
    },
    dispatch,
  ] = useReducer(reducer, initialState);

  const [isOpen, setIsOpen] = useState(false);
  const handleOpen = () => setIsOpen(true);

  const handleShareWhatsApp = () => {
    const text = `${productName}\n${shareYouWillNeedLabel} ${estimatedLitres} ${litresLabel}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShareEmail = () => {
    const subject = productName;
    const path = productUrl;
    const url = `${window.location.origin}${path}`;
    const body = `${productName}: ${url}\n${shareYouWillNeedLabel} ${estimatedLitres} ${litresLabel}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const recalc = useCallback(() => {
    const { displayArea, litres }: PaintResult = calculatePaint(
      walls,
      doorsCount,
      windowsCount,
      unit as 'm' | 'ft',
      Number(productPaintCoverageValue),
      Number(productPaintCoatsValue),
    );

    dispatch({ type: ActionType.SET_ESTIMATED_AREA, payload: displayArea });
    dispatch({ type: ActionType.SET_ESTIMATED_LITRES, payload: litres });
  }, [walls, doorsCount, windowsCount, unit, productPaintCoverageValue]);

  useEffect(() => {
    recalc();
  }, [recalc]);

  useEffect(() => {
    const defaultWall =
      activeTab === 'area'
        ? { length: '', height: '', width: '' }
        : { length: '', height: '' };

    dispatch({
      type: ActionType.SET_WALLS,
      payload: [defaultWall],
    });
  }, [activeTab]);

  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: ActionType.RESET });
    }
  }, [isOpen]);

  const formLabels: PaintCalculatorLabels = {
    addAnotherAreaCtaLabel,
    addAnotherWallCtaLabel,
    addDoorAndWindowsCtaLabel,
    decreaseAriaLabel,
    doorsLabel,
    heightLabel,
    heightOrWidthLabel,
    increaseAriaLabel,
    lengthLabel,
    metricFeetLabel,
    metricMeterLabel,
    metricMeterSquareLabel,
    removeAreaCtaLabel,
    removeWallCtaLabel,
    widthLabel,
    windowsLabel,
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        label={ctaLabel}
        buttonClasses="tw-basis-6/12"
        variant="grey80"
        modifier="outline"
      />
      <DrawerWrapper
        title={title}
        cta={makeCrazyCalculation}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        sizeVariant="large"
        wrapperStyles="tw-h-full tw-flex tw-flex-col"
        containerClasses="tw-flex tw-flex-col"
        visibleOverflow
        animateVerticallyOnMobile={false}
      >
        <div className="tw-flex tw-h-full tw-flex-col">
          {/* Scrollable content wrapper */}
          <div className="tw-flex tw-grow tw-flex-col tw-justify-between">
            <div className="tw-px-4 tw-py-10 md:tw-px-12">
              <div className="tw-flex tw-justify-between">
                {/* Tabs */}
                <div className="tw-flex tw-w-auto tw-justify-between tw-gap-8 tw-text-sm">
                  <button
                    className={`tw-relative tw-border-b-2 tw-border-transparent tw-py-1.5 ${activeTab === 'surface' ? '!tw-border-black tw-font-semibold' : 'tw-text-grey-70'}`}
                    onClick={() =>
                      dispatch({
                        type: ActionType.SET_ACTIVE_TAB,
                        payload: 'surface',
                      })
                    }
                  >
                    {bySurfaceTabLabel}
                  </button>
                  <button
                    className={`tw-relative tw-border-b-2 tw-border-transparent tw-py-1.5 ${activeTab === 'area' ? '!tw-border-black tw-font-semibold' : 'tw-text-grey-70'}`}
                    onClick={() =>
                      dispatch({
                        type: ActionType.SET_ACTIVE_TAB,
                        payload: 'area',
                      })
                    }
                  >
                    {byAreaTabLabel}
                  </button>
                </div>
                {/* Unit toggle */}
                <Toggle
                  options={[
                    { label: metricMeterLabel, value: 'm' },
                    { label: metricFeetLabel, value: 'ft' },
                  ]}
                  value={unit}
                  onChange={(val) =>
                    dispatch({
                      type: ActionType.SET_UNIT,
                      payload: val as PaintCalculatorState['unit'],
                    })
                  }
                  label={unitTextLabel}
                />
              </div>
              {/* Description */}
              <div className="tw-pb-8 tw-pt-4 tw-text-sm tw-text-grey-70 md:tw-pb-10">
                {activeTab === 'surface'
                  ? bySurfaceTabDescription
                  : byAreaTabDescription}
              </div>
              {/* Input section */}
              <WallsFormSection
                activeTab={activeTab}
                addWall={() =>
                  dispatch({
                    type: ActionType.SET_WALLS,
                    payload: [...walls, { length: '', height: '' }],
                  })
                }
                dispatch={dispatch}
                doorsCount={doorsCount}
                labels={formLabels}
                onSubmit={recalc}
                removeWall={() =>
                  dispatch({
                    type: ActionType.SET_WALLS,
                    payload: walls.slice(0, -1),
                  })
                }
                showOpenings={showOpenings}
                unit={unit}
                walls={walls}
                windowsCount={windowsCount}
              />
            </div>
            <Text className="tw-px-4 tw-py-4 tw-text-center tw-font-sans tw-text-sm tw-italic tw-text-grey-70 md:tw-py-6">
              {resultDisclaimerText}
            </Text>
          </div>
          {/* Results footer */}
          <div className="tw-sticky tw-bottom-0 tw-mt-auto tw-border-t tw-border-grey-30 tw-bg-white tw-p-4">
            <div className="tw-mt-2 tw-flex tw-flex-col tw-text-sm tw-text-grey-70">
              <div className="tw-flex tw-justify-between">
                <Text variant="body-small">{estimatedLabel}</Text>
                <Text variant="body-small">
                  {estimatedArea.toString()}&nbsp;
                  {unit === 'm' ? metricMeterSquareLabel : metricFeetLabel}
                </Text>
              </div>
              <div className="tw-flex tw-justify-between">
                <Text variant="body-small">{paintCoverageLabel}</Text>
                <Text variant="body-small">
                  {productPaintCoverageValue}&nbsp;
                  {metricMeterSquareLabel}
                </Text>
              </div>
            </div>
            <div className="tw-mt-4 tw-flex tw-items-end tw-justify-between">
              <Text variant="ds-headline-4">{weThinkWillNeedLabel}</Text>
              <Text variant="ds-headline-4">
                {estimatedLitres.toString()} {litresLabel}
              </Text>
            </div>
            {/* Share section */}
            <div className="tw-mt-4 tw-flex tw-gap-4">
              <Button
                label={shareWhatsAppCtaLabel}
                onClick={handleShareWhatsApp}
                modifier="outline"
                variant="grey80"
                buttonClasses="tw-flex-1"
                iconName="whatsapp"
              />
              <Button
                label={sendToEmailCtaLabel}
                onClick={handleShareEmail}
                modifier="fill"
                variant="grey80"
                buttonClasses="md:tw-flex-1"
                iconName="mail"
              />
            </div>
          </div>
        </div>
      </DrawerWrapper>
    </>
  );
};

export default PaintCalculator;
